import { useMemo } from 'react';

/**
 * Calculate performance indicators for a single gym
 * @param {Object} gymData - gym data object with pulse and metrics information
 * @returns {Object} - { problems: [], excellent: [] } arrays of indicator identifiers
 */
export function getPerformanceIndicators(gymData) {
  const problems = [];
  const excellent = [];

  // Red flag problems
  // Check for missing pulses (status must be 'filled')
  const p1_filled = gymData.p1_status === 'filled';
  const p2_filled = gymData.p2_status === 'filled';
  const p3_filled = gymData.p3_status === 'filled';
  const p4_filled = gymData.p4_status === 'filled';

  if (!p1_filled || !p2_filled || !p3_filled || !p4_filled) {
    problems.push('missing_pulses');
  }

  // Check for low connect rate (< 40%)
  if (gymData.connect_rate !== null && gymData.connect_rate !== undefined && gymData.connect_rate < 40) {
    problems.push('low_connect_rate');
  }

  // Check for zero closures with activity
  if (gymData.closures === 0 && gymData.dialed > 0) {
    problems.push('zero_closures');
  }

  // Check for no front desk availability
  if (gymData.fd_availability === 0) {
    problems.push('no_fd');
  }

  // Green flag excellent performance
  // All pulses filled
  if (p1_filled && p2_filled && p3_filled && p4_filled) {
    excellent.push('all_pulses_filled');
  }

  // High connect rate (> 70%)
  if (gymData.connect_rate !== null && gymData.connect_rate !== undefined && gymData.connect_rate > 70) {
    excellent.push('high_connect_rate');
  }

  // Exceeds closure target
  if (gymData.closures > (gymData.target_closures || 0)) {
    excellent.push('exceeds_target');
  }

  return { problems, excellent };
}

/**
 * Hook to calculate performance indicators for all gyms in dataset
 * @param {Array} data - array of gym data objects
 * @returns {Array} - array of gym objects with added indicators property
 */
export function usePerformanceMetrics(data) {
  return useMemo(() => {
    return data.map(gym => ({
      ...gym,
      indicators: getPerformanceIndicators(gym)
    }));
  }, [data]);
}
